import { create } from 'zustand';
import { PersistOptions, persist } from 'zustand/middleware';

import { REACT_APP_KK_CUSTOMER_ID_EXPIRE_MS, StorageNames } from '@/lib/constants';

// the store for managing products in the cart

type CustomerFailureCode = 0 | 1 | 2 | '0' | '1' | '2' | null;

interface CustomerStore {
  /* This is the customerId. */
  customerId: string;
  checkedEmail?: string;
  customerFailureCode?: CustomerFailureCode;
  kkExpireAt?: number;
  isLoggedIn?: boolean;
  sessionId: string;
  nextStep?: string;
  ctAPIStatus?: boolean;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setCustomerId: (id: string) => void;
  setKkExpireAt: (
    lastModifiedAt?: string | null,
    deleteDaysAfterLastModification?: string | number | null,
    ctAPIStatus?: boolean
  ) => void;
  setCustomerDetails: (
    nextStep: string,
    checkedEmail?: string,
    customerFailureCode?: CustomerFailureCode
  ) => void;
  resetCustomerDetails: () => void;
  clearCustomerStore: () => void;
}

// Define what part of the state to persist
type PersistCustomerState = Pick<
  CustomerStore,
  'customerId' | 'nextStep' | 'checkedEmail' | 'customerFailureCode'
>;

const persistOptions: PersistOptions<CustomerStore, PersistCustomerState> = {
  name: StorageNames.CUSTOMER_STORAGE,
  partialize: (state: CustomerStore) => ({
    customerId: state.customerId,
    checkedEmail: state.checkedEmail,
    customerFailureCode: state.customerFailureCode,
    kkExpireAt: state.kkExpireAt,
    isLoggedIn: state.isLoggedIn,
    sessionId: state.sessionId,
    nextStep: state.nextStep,
    ctAPIStatus: state.ctAPIStatus,
  }),
};

const millisecondsInDay = 24 * 60 * 60 * 1000;

const getKkExpireAt = (
  lastModifiedAt?: string | null,
  deleteDaysAfterLastModification?: string | number | null
): number => {
  const defaultKkExpireAt = () => Number(REACT_APP_KK_CUSTOMER_ID_EXPIRE_MS) + Date.now();

  if (lastModifiedAt == null || deleteDaysAfterLastModification == null) {
    return defaultKkExpireAt();
  }

  const lastModifiedAtMs = new Date(lastModifiedAt).getTime();
  const deleteDays = Number(deleteDaysAfterLastModification);

  if (!Number.isFinite(lastModifiedAtMs) || !Number.isFinite(deleteDays)) {
    return defaultKkExpireAt();
  }
  const expiryAt = lastModifiedAtMs + deleteDays * millisecondsInDay - millisecondsInDay;

  return expiryAt;
};

// Create the Zustand store with persistence
export const useCustomerStore = create<CustomerStore>()(
  persist(
    set => ({
      customerId: '',
      checkedEmail: '',
      customerFailureCode: null,
      kkExpireAt: 0,
      sessionId: '',
      nextStep: '',
      isLoggedIn: false,
      ctAPIStatus: false,
      setCustomerId: id =>
        set({
          customerId: id,
          sessionId: '',
          kkExpireAt: REACT_APP_KK_CUSTOMER_ID_EXPIRE_MS + Date.now(),
        }),

      setKkExpireAt: (lastModifiedAt, deleteDaysAfterLastModification, ctAPIStatus) => {
        const kkExpireAt = getKkExpireAt(lastModifiedAt, deleteDaysAfterLastModification);
        set({ kkExpireAt, ctAPIStatus });
      },

      setCustomerDetails: (
        nextStep: string,
        checkedEmail = '',
        customerFailureCode: CustomerFailureCode = null
      ) => {
        set({
          checkedEmail,
          customerFailureCode,
          nextStep: nextStep,
        });
      },
      resetCustomerDetails: () => {
        set({
          checkedEmail: '',
          customerFailureCode: null,
          nextStep: '',
        });
      },
      setIsLoggedIn: (value: boolean) => {
        set({
          isLoggedIn: value,
        });
      },
      clearCustomerStore: () => {
        set({
          customerId: '',
          checkedEmail: '',
          customerFailureCode: null,
          kkExpireAt: 0,
          sessionId: '',
          nextStep: '',
          isLoggedIn: false,
          ctAPIStatus: false,
        });
      },
    }),
    persistOptions
  )
);
